/**
 * What the host has told us about who sits with whom, as one list of edges.
 *
 * This is a TypeScript twin of `GroupGuestBonds.Resolve` in OrchestratorAPI
 * (Domain/Models/Groups/GroupGuestTagModels.cs), which is the specification and
 * carries the reasoning. It exists here because the tracker board already hands
 * the browser both inputs (`links` and each guest's `plusOneOfInviteeId`) and
 * the seater runs in the browser, so there is no server call to make. The rules
 * are copied, not re-decided — and lib/seatingBonds.test.ts replays the server's
 * own Resolve_* cases so the two cannot drift without a test failing:
 *
 *  - An explicit link claims its pair, INCLUDING a link whose seating is 'any'.
 *    A host who recorded "exes, seating doesn't matter" has answered, and the
 *    plus-one pass must not re-assert "together" underneath that answer.
 *  - A plus-one (invited on someone's invitation) implies a WEAK 'together':
 *    nobody asserted it, so it is 'prefer', never 'must'.
 *  - A self-referential plus-one is corrupt data, not a bond.
 */
import type { GuestLink, TrackerGuest } from '../services/tracker-api-client';

export type BondSeating = 'together' | 'apart';
export type BondStrength = 'must' | 'prefer';

export interface SeatBond {
  /** The lower of the two ids, as a string compare — a stable pair key, nothing more. */
  a: string;
  b: string;
  seating: BondSeating;
  strength: BondStrength;
  source: 'link' | 'plus_one';
}

const order = (x: string, y: string): [string, string] => (x <= y ? [x, y] : [y, x]);
const pairKey = (x: string, y: string) => order(x, y).join('|');

export function resolveBonds(
  links: ReadonlyArray<Pick<GuestLink, 'inviteeId' | 'otherId' | 'seating' | 'strength'>> | null | undefined,
  roster: ReadonlyArray<Pick<TrackerGuest, 'inviteeId' | 'plusOneOfInviteeId'>> | null | undefined,
): SeatBond[] {
  const bonds: SeatBond[] = [];
  const claimed = new Set<string>();

  for (const link of links ?? []) {
    const key = pairKey(link.inviteeId, link.otherId);
    if (claimed.has(key)) continue;
    claimed.add(key);

    // 'any' (or anything the seater has no instruction for) claims the pair
    // without producing a bond.
    if (link.seating !== 'together' && link.seating !== 'apart') continue;
    const [a, b] = order(link.inviteeId, link.otherId);
    bonds.push({
      a,
      b,
      seating: link.seating,
      // The server's default is 'must'; only an explicit 'prefer' softens it.
      strength: link.strength === 'prefer' ? 'prefer' : 'must',
      source: 'link',
    });
  }

  for (const guest of roster ?? []) {
    const parent = guest.plusOneOfInviteeId;
    if (!parent || parent === guest.inviteeId) continue;
    const key = pairKey(guest.inviteeId, parent);
    if (claimed.has(key)) continue;
    claimed.add(key);
    const [a, b] = order(guest.inviteeId, parent);
    bonds.push({ a, b, seating: 'together', strength: 'prefer', source: 'plus_one' });
  }

  return bonds;
}
